import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import TagManager from 'react-gtm-module';
import { useRouter } from 'next/router';
import DOMPurify from 'isomorphic-dompurify';
import CardBenefit from 'components/benefits/CardBenefit';
import LayoutContainer from 'components/layout/LayoutContainer';
import { Breadcrumbs } from 'components/widgets';

import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as actions from '../../store/actions';
import ProtectComponent from '../../src/components/benefits/ProtectComponent';

const sanitizer = DOMPurify.sanitize;
const serverUrlImages = process.env.REACT_APP_SERVER_URL_IMAGES;
const telemedicine = `${serverUrlImages}/icons/benefits-page/telemedicina.svg`;
const house = `${serverUrlImages}/icons/benefits-page/medicina-encasa.svg`;
const ambulance = `${serverUrlImages}/icons/benefits-page/ambulance.svg`;
const localHospital = `${serverUrlImages}/icons/benefits-page/local-hospital.svg`;
const consultations = `${serverUrlImages}/icons/benefits-page/consultas-rd.svg`;
const medicalLabs = `${serverUrlImages}/icons/benefits-page/labs-rd.svg`;
const medicalExams = `${serverUrlImages}/icons/ASIS-Icono-LecturaExamenesMedicos.png`;
const secondOpinion = `${serverUrlImages}/icons/ASIS-Icono-SegundaOpinionMedica.png`;
const doctor = `${serverUrlImages}/icons/doctor.svg`;
const covidService = `${serverUrlImages}/icons/covid-benefit.png`;

const addonStar = `${serverUrlImages}/icons/beneficios-adicionales-icono.svg`;
const proMed = `${serverUrlImages}/RD/image/promed.png`;
const humano = `${serverUrlImages}/RD/humano.svg`;
const amadita = `${serverUrlImages}/RD/image/amadita.png`;
const medicines = `${serverUrlImages}/icons/medicine_icon.svg`;
const boton = `${serverUrlImages}/icons/boton-de-asistensia-icon.svg`;
const samambulancia = `${serverUrlImages}/RD/image/samambulancia.png`;

const BenefitsPage = (props) => {
	const router = useRouter();
	const canonicalURL =
		process.env.REACT_APP_DOMAIN_URL + (router?.asPath || router?.pathname);

	const [routes] = useState([
		{
			name: 'Inicio',
			pathname: '/',
			asPath: '/'
		},
		{
			name: 'Servicios de salud',
			pathname: router?.pathname,
			asPath: router?.asPath
		}
	]);

	useEffect(() => {
		const tagManagerArgs = {
			dataLayer: {
				event: 'pageview',
				pageTitle: 'Beneficios del mejor seguro de emergencias médicas'
			}
		};
		TagManager.dataLayer(tagManagerArgs);

		getActivePromotions();
	}, []);

	const getActivePromotions = async () =>
		props.actions.getActivePromotions();

	const TextAsistensi =
		'<span class="text-pink-500 font-bold font-poppins">asistensi</span>';

	const benefits = {
		telemedicine: {
			title: 'Telemedicina ilimitada',
			srcImage: consultations,
			paragraph:
			`Contacta con un médico dominicano en menos de 20 segundos, siempre que lo necesites y desde cualquier lugar.  </br> </br>
			Nuestros médicos están disponibles las 24 horas del día sin cita previa, por teléfono o videollamada a través de la App asistensi, para orientar ante cualquier situación médica y acompañar durante la solicitud de los servicios de salud del plan ${TextAsistensi}. <br/> <br/>
			Además, nuestros doctores proveen un servicio de acompañamiento médico, ofreciendo un trato personalizado al emitir recetas, enviar medicamentos y solicitar pruebas de laboratorio para mejorar el diagnóstico. <br/><br/>
			De ser necesario, también se encargan de gestionar la atención médica domiciliaria y el traslado en ambulancias, y de apoyar en la admisión a clínicas afiliadas activando la cobertura con Humano Seguros.`
		},
		ambulances: {
			title: 'Traslados en ambulancia',
			srcImage: ambulance,
			paragraph: `En caso de urgencia médica y necesitar traslado de emergencias hacia una clínica afiliada al seguro, nuestros doctores coordinarán el envío de ambulancias equipadas con soporte avanzado de vida. De ser necesario también podemos gestionar el retorno a casa una vez dada el alta médica.
						<div class="w-64 h-auto mx-auto mt-2"> <img src=${proMed} width="" height="" > </div>
						<div class="w-52 h-auto mx-auto mt-4"> <img src=${samambulancia} width="" height="" > </div>
						`
		},
		homeHealthCare: {
			title: 'Atención médica domiciliaria',
			srcImage: house,
			paragraph: `En los casos en que sea necesaria una consulta presencial, nuestros médicos y paramédicos realizan visitas domiciliarias para determinar mejor las necesidades del paciente, hacer un diagnóstico e indicar tratamiento. El equipo médico podrá incluso colocar dosis únicas de medicamentos.<br/><br/>
						Nuestro servicio de asistencia domiciliaria está disponible las 24 horas, los 365 días del año.
						<div class="w-64 h-auto mx-auto mt-2"> <img src=${proMed} width="" height="" > </div>
						<div class="w-52 h-auto mx-auto mt-10"> <img src=${samambulancia} width="" height="" > </div>
						`
		},
		coverage: {
			title: 'Seguro de hospitalización para emergencias médicas por enfermedades y accidentes',
			srcImage: localHospital,
			paragraph: `Brinda acceso a centros de salud privados y en los mismos, no se solicitarán pagos relacionados a garantías, deducibles y coaseguros.</br></br>
						Este plan cuenta con el respaldo y experiencia de Humano Seguros.</br> </br>
						Incluye cobertura de gastos médicos hasta RD $350,000 con el plan ${TextAsistensi}. Con el plan ${TextAsistensi} Plus el monto sube hasta RD $600,000, suficiente para cubrir más del 90% de los casos más comunes.</br> </br>
						Consulta el listado de centros de salud a los que puedes acceder <a href="faq/?q=56" class="font-poppins font-bold">aquí</a>.
						<div class="w-52 h-auto mx-auto"> <img src=${humano} width="" height="" > </div>`
		},
		consultations: {
			title: 'Consulta con especialistas',
			srcImage: doctor,
			paragraph:
			`Incluye 2 consultas al año con especialistas (4 con el plan ${TextAsistensi} Plus) de cualquier área médica.`
		},
		laboratory: {
			title: 'Estudios de laboratorio',
			srcImage: medicalLabs,
			paragraph: `Para poder recibir un diagnóstico y tratamiento médico óptimo, cuentas con 2 estudios de laboratorios clínicos al año (4 con ${TextAsistensi} Plus) siempre y cuando nuestros médicos los consideren necesarios y estén incluidos en el plan.</br></br>
						Porque sabemos que mereces recibir el mejor cuidado, todos los estudios de laboratorio los podrás realizar en alguna de las múltiples sucursales de laboratorios Amadita.</br></br>
						<div class="w-52 h-auto mx-auto"> <img src=${amadita} width="" height="" ></div>`
		},
		covidService: {
			title: 'Servicios de COVID-19',
			srcImage: covidService,
			paragraph: `También ofrecemos cobertura frente al COVID-19 a través de niveles de atención especializada:</br></br>
			<ul class="list-disc">
				<li class="font-poppins">Acompañamiento médico remoto continuo desde el momento del primer síntoma.</li>
				<li class="font-poppins">Cobertura de prueba PCR, en caso de hospitalización por síntomas respiratorios cuando existe sospecha de COVID-19, siempre que sea indicada por nuestros médicos.</li>
			</ul>`
		}
	};

	const addons = {
		boton: {
			title: 'Botón de asistencia médica',
			srcImage: boton,
			paragraph: `
				<div class="text-gray-750 font-poppins">
					Protección inmediata con un dispositivo portátil de última generación para solicitar asistencia médica con solo presionar un botón.
				</div>
				<div class="mt-5">
					<p class="font-poppins">El beneficiario recibirá un dispositivo que le proporciona:</p>
					<ul class="font-poppins mt-5 px-5">
						<li class="list-disc ml-5 font-poppins">Conexión inmediata con los médicos de cabecera virtual.</li>
						<li class="list-disc ml-5 font-poppins">Sistema geolocalizable que garantiza la rápida ubicación del beneficiario.</li>
						<li class="list-disc ml-5 font-poppins">Una tarjeta SIM, por lo tanto, no necesita tener un teléfono para recibir asistencia médica.</li>
					</ul>
				</div>
				`
		},
		medicine: {
			title: 'Entrega o recogida de medicamentos',
			srcImage: medicines,
			paragraph: `
				<div class=" font-poppins text-base leading-relaxed text-gray-750">
				Para poder tratarte frente a los malestares del día a día, las enfermedades comunes y emergencias médicas, cuentas con medicamentos que te entregamos en tu casa o que podrás recoger en los puntos de venta autorizados, siempre que hayan sido previamente recetados y estén incluidos en el plan. No están incluidos los medicamentos para enfermedades crónicas.
				</div>
				`
		}
	};

	const { activePromotions } = props;
	const promoToShow =
		activePromotions && activePromotions.active === true
			? activePromotions.configuration.find((config) => {
					return config.position === 'benefits-page';
			  })
			: null;

	return (
		<LayoutContainer>
			<Head>
				<title>Beneficios del mejor seguro de emergencias médicas</title>
				<meta
					name="description"
					content="Con asistensi tu familia puede cuidar su salud a la hora de una emergencia médica"
				/>
				<link rel="canonical" href={canonicalURL} />
			</Head>

			<div className="mt-12 h-full">
				<div className="bg-purple-500 flex flex-col items-center justify-center py-16 text-center">
					<h2 className="text-pink-500 font-bold text-base text-center font-poppins container mb-4">
						¡24 horas del día, los 365 días del año!
					</h2>
					<h1 className="font-poppins container text-white font-bold leading-snug text-4xl sm:text-5xl">
						Nuestros servicios de salud
					</h1>
				</div>
				<Breadcrumbs routes={routes} />
				<div className="container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 py-6">
					<CardBenefit {...benefits.telemedicine} />
					<CardBenefit {...benefits.homeHealthCare} />
					<CardBenefit {...benefits.coverage} />
					<CardBenefit {...benefits.ambulances} />
					<CardBenefit {...benefits.laboratory} />
					<CardBenefit {...benefits.consultations} />


					<CardBenefit {...benefits.covidService} />
				</div>


				<ProtectComponent/>
				
				{/* new addons */}
				<div className="mb-16 container flex flex-col sm:flex-row rounded border border-gray-300 bg-purple-200 px-6 py-8 justify-center">
					<div className="mx-8 flex justify-center">
						<img
							className="object-scale-down "
							alt="icono estrella"
							width="100"
							height="80"
							src={addonStar}
						/>
					</div>
					<p className="text-2xl md:text-3xl text-purple-500 font-bold font-poppins mt-2 w-full sm:w-6/12 leading-9 text-center md:text-left">
						<span className="text-pink-500 font-boldn font-poppins">
							Beneficios adicionales
						</span>{' '}
						que puedes contratar con{' '}
						<span className="text-pink-500 font-bold font-poppins">
							tu plan asistensi:
						</span>
					</p>
				</div>

				<div className="container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 py-6">
					<CardBenefit
						{...addons.medicine}
						borderImg={false}
						heightAuto={true}
					/>
					<CardBenefit {...addons.boton} borderImg={false} heightAuto={true} />
				</div>

				{promoToShow && (
					<div
						className="container mb-16"
						dangerouslySetInnerHTML={{
							__html: sanitizer(promoToShow.description)
						}}
					></div>
				)}

				{/* <div className="flex mx-2 sm:mx-0 justify-center mb-16">
					<Link href="/quotation" as="/cotizacion">
						<a className="primary-button max-w-small sm:max-w-none">
							<span className="sm:hidden">¡Cotizar!</span>
							<span className="hidden sm:inline">¡Quiero cotizar!</span>
						</a>
					</Link>
					<a
						className="secondary-button max-w-small sm:max-w-none ml-8"
						onClick={() =>
							router.push('/affiliation', '/comenzar-afiliacion', {
								signIn: true
							})
						}
					>
						<span className="sm:hidden">¡Contratar!</span>
						<span className="hidden sm:inline">¡Contratar ahora!</span>
					</a>
				</div> */}
			</div>
		</LayoutContainer>
	);
};

const mapStateToProps = (state) => ({
	activePromotions: state.product.get('activePromotions')
});

const mapDispatchToProps = (dispatch) => ({
	actions: bindActionCreators(actions, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(BenefitsPage);
